import { useEffect, useMemo, useState } from 'react';

import {
  isUnusableE2eUrl,
  mergeReportImages,
  normalizeReportImages,
  reportImagesMissingViews,
  reportImagesNeedHydration,
} from '../utils/e2eMedia';
import { fetchCapturedViews } from '../utils/skinReportApi';

/**
 * Prefer decrypted data URLs from /api/captured-views when report images are
 * presigned E2E links or unresolved skin/ keys.
 */
export function useReportImages(report, skinReference, appSessionToken) {
  const [capturedViews, setCapturedViews] = useState(null);
  const [hydrateError, setHydrateError] = useState(null);

  const reportImages = useMemo(
    () => normalizeReportImages(report?.images || {}),
    [report?.images],
  );

  const needsHydration = useMemo(
    () => reportImagesNeedHydration(reportImages),
    [reportImages],
  );

  const shouldFetchCapturedViews = useMemo(
    () => needsHydration || reportImagesMissingViews(reportImages),
    [needsHydration, reportImages],
  );

  useEffect(() => {
    const ref = String(skinReference || '').trim();
    if (!ref || !shouldFetchCapturedViews) {
      setCapturedViews(null);
      setHydrateError(null);
      return undefined;
    }

    let cancelled = false;
    setHydrateError(null);

    fetchCapturedViews(ref, { appSessionToken: appSessionToken || undefined })
      .then((data) => {
        if (cancelled) return;
        setCapturedViews(normalizeReportImages(data?.captured_views || {}));
      })
      .catch((err) => {
        if (!cancelled) {
          setCapturedViews(null);
          setHydrateError(err.message || 'Failed to hydrate images');
        }
      });

    return () => { cancelled = true; };
  }, [skinReference, appSessionToken, shouldFetchCapturedViews]);

  const images = useMemo(
    () => mergeReportImages(reportImages, capturedViews || {}),
    [reportImages, capturedViews],
  );

  const hydrating = shouldFetchCapturedViews && !capturedViews && !hydrateError;
  const hasDisplayableImage = useMemo(
    () => Object.values(images).some((url) => url && !isUnusableE2eUrl(url)),
    [images],
  );

  return {
    images,
    needsHydration,
    hydrateError,
    hydrating,
    hasDisplayableImage,
  };
}
